import * as t from './actionTypes';
import { NAME } from './constants';
import { inputObjectDefaults } from './data-constants';
import {
  filterQuoteOnlyStatuses,
  getExportForPolling,
  isPollableState,
  parseCurrentQuoteResult,
  validationCallbacks,
  toDateIsNotBeforeFromDate,
  validateInputsOnFormSubmit,
  validateMultiSelectsOnFormSubmit,
} from './selectors';

const dispatchOnlyType = type => ({
  type,
});

export const setSavedState = newState => dispatch => dispatch({
  type: t.SET_SAVED_STATE,
  result: {
    ...newState,
  },
});

export const setDataExportTypeKey = exportTypeKey => dispatch => dispatch({
  type: t.SET_EXPORT_TYPE,
  exportTypeKey,
});

export const generateDataExport = ({ id, checked = false, paymentNonce = null }) =>
  async (dispatch, getStore, client) => {
    if (!paymentNonce) {
      return dispatch({
        type: t.LOAD_CREATE_EXPORT_FAIL_VALIDATION,
        result: { error: 'MISSING_NONCE' },
      });
    } else if (!checked) {
      return dispatch({
        type: t.LOAD_CREATE_EXPORT_FAIL_VALIDATION,
        result: { error: 'AGREE_TERMS' },
      });
    } else if (!id) {
      return dispatch({
        type: t.LOAD_CREATE_EXPORT_FAIL_VALIDATION,
        result: { error: 'MISSING_ID' },
      });
    }

    dispatch(dispatchOnlyType(t.LOAD_CREATE_EXPORT));

    try {
      const data = { paymentNonce };
      const url = `/data-exports/${id}/generate`;
      const result = await client.put(url, { data });
      return dispatch({
        type: t.LOAD_CREATE_EXPORT_SUCCESS,
        result,
      });
    } catch (err) {
      return dispatch(dispatchOnlyType(t.LOAD_CREATE_EXPORT_FAIL));
    }
  };

export const resetPurchaseExportData = id =>
  dispatch => dispatch({
    type: t.RESET_PURCHASE_EXPORT_DATA,
    result: {
      // RESET ALL FORM FIELDS
      airlines: [],
      arrivalAirports: [],
      departureAirports: [],
      arrivalCountries: [],
      departureCountries: [],
      arrivalRegions: [],
      departureRegions: [],
      arrivalCheckedSearch: 'airport',
      departureCheckedSearch: 'airport',
      arrivalCheckedSearchInvalid: null,
      departureCheckedSearchInvalid: null,
      // KEEP REF TO THE CREATED EXPORT FOR STATUS POLLING
      currentlyGeneratingExportId: id,
      // CLEAR CREATE EXPORT
      createDataExport: null,
      createDataExportError: null,
      createDataExportLoading: false,
      // CLEAR PREVIOUS QUOTE
      dataExportQuoteCurrentEstimate: null,
    },
  });

export const clearDataExportError = () => dispatch =>
  dispatch(dispatchOnlyType(t.LOAD_CREATE_EXPORT_CLEAR_ERROR));

export const validateTermsChecked = checked => (dispatch) => {
  if (checked) {
    return dispatch(dispatchOnlyType(t.LOAD_CREATE_EXPORT_CLEAR_ERROR));
  }
  const result = {
    error: 'AGREE_TERMS',
  };
  return dispatch({
    type: t.LOAD_CREATE_EXPORT_FAIL_VALIDATION,
    result,
  });
};

export const createDataExportQuote = ({
  metadata,
  query,
  type,
  userIntendedUse,
  userIndustry,
  pollForFinalQuoteData,
}) =>
  async (dispatch, getStore, client) => {
    const data = {
      metadata,
      query,
      type,
      userIntendedUse,
      userIndustry,
    };

    const datesValid = toDateIsNotBeforeFromDate(query);
    const url = '/data-exports/quote';
    if (datesValid !== 'Invalid Date') {
      dispatch(dispatchOnlyType(t.LOAD_CREATE_EXPORT_QUOTE));
      try {
        const result = await client.post(url, { data });
        if (result && result.data && result.data.id) {
          pollForFinalQuoteData(result.data.id);
        }
        const transformedResult = parseCurrentQuoteResult(result);
        return dispatch({
          type: t.LOAD_CREATE_EXPORT_QUOTE_SUCCESS,
          result: transformedResult,
        });
      } catch (err) {
        console.log('error', err);
        return dispatch(dispatchOnlyType(t.LOAD_CREATE_EXPORT_QUOTE_FAIL));
      }
    }
    const result = {
      invlalidForm: true,
      exportFormEndDateInvalid: datesValid,
      exportFormStartDateInvalid: datesValid,
    };
    return dispatch({
      type: t.LOAD_CREATE_EXPORT_QUOTE_SUCCESS,
      result,
    });
  };

export const getDataExportById = currentEstimateId =>
  async (dispatch, getStore, client) => {
    const url = `/data-export/${currentEstimateId}`;
    dispatch(dispatchOnlyType(t.LOAD_EXPORT_FINAL_QUOTE));
    try {
      const result = await client.get(url);
      return dispatch({
        type: t.LOAD_EXPORT_FINAL_QUOTE_SUCCESS,
        result,
      });
    } catch (err) {
      console.log(err);
      return dispatch(dispatchOnlyType(t.LOAD_EXPORT_FINAL_QUOTE_FAIL));
    }
  };

export const getAllDataExportForUser = pollForRecentStatusChange =>
  async (dispatch, getStore, client) => {
    const url = '/data-exports';
    dispatch(dispatchOnlyType(t.LOAD_DATA_EXPORTS_FOR_USER));
    try {
      const result = await client.get(url);
      const { data } = result;
      const store = getStore();
      const currentId = store && store[NAME] && store[NAME].currentlyGeneratingExportId;
      const filteredData = filterQuoteOnlyStatuses(data, currentId);
      // is there a reason to start polling ? let's check that first...
      if (pollForRecentStatusChange && filteredData && isPollableState(filteredData)) {
      // ok let's poll
        const pollingExport = currentId ? getExportForPolling(filteredData, currentId) : null;
        if (pollingExport) {
          pollForRecentStatusChange(pollingExport);
        }
      }
      return dispatch({
        type: t.LOAD_DATA_EXPORTS_FOR_USER_SUCCESS,
        result: filteredData,
      });
    } catch (err) {
      return dispatch({
        type: t.LOAD_DATA_EXPORTS_FOR_USER_FAIL,
        result: err,
      });
    }
  };

export const getDataExportTypes = () =>
  async (dispatch, getStore, client) => {
    const url = '/data-exports/types';
    dispatch(dispatchOnlyType(t.LOAD_DATA_EXPORT_TYPES));
    try {
      const result = await client.get(url);
      return dispatch({
        type: t.LOAD_DATA_EXPORT_TYPES_SUCCESS,
        result,
      });
    } catch (err) {
      console.log('error', err);
      return dispatch(dispatchOnlyType(t.LOAD_DATA_EXPORT_TYPES_FAIL));
    }
  };

export const clearPreviousQuote = () => dispatch => dispatch({
  type: t.CLEAR_PREVIOUS_QUOTE,
  result: {
    dataExportQuoteCurrentEstimate: null,
    overDataLimit: false,
    underDataMinimum: false,
  },
});

export const setPollingStatus = bool => dispatch => dispatch({
  type: t.SET_POLLING_STATUS,
  result: {
    polling: bool,
  },
});

export const handleTextFieldValue = (fieldName, value) =>
  dispatch => dispatch({ type: t.UPDATE_TEXT_FIELD_VALUE, result: { [fieldName]: value } });

export const resetAllFormFields = () => (dispatch) => {
  const result = {
    airlines: [],
    arrivalAirports: [],
    departureAirports: [],
    arrivalCountries: [],
    departureCountries: [],
    arrivalRegions: [],
    departureRegions: [],
    arrivalCheckedSearch: 'airport',
    departureCheckedSearch: 'airport',
    arrivalCheckedSearchInvalid: null,
    departureCheckedSearchInvalid: null,
  };
  Object.keys(inputObjectDefaults).forEach((key) => {
    result[inputObjectDefaults[key].name] = inputObjectDefaults[key].value;
    result[`${inputObjectDefaults[key].name}Invalid`] = null;
  });
  result.invalidForm = true;
  return dispatch({
    type: t.RESET_ALL_FORM_FIELDS,
    result,
  });
};

export const handleFormSubmitValidation = (globalState, exportType) => (dispatch) => {
  const inputResult = validateInputsOnFormSubmit(globalState);
  const selectsResult = validateMultiSelectsOnFormSubmit(globalState, exportType);
  const invalidValues = {
    invalidValues: selectsResult.invalidValues.concat(inputResult.invalidValues),
  };
  const result = {
    ...inputResult,
    ...selectsResult,
    invalidForm: inputResult.invalidForm || selectsResult.invalidForm,
    ...invalidValues,
  };

  if (result.invalidForm) {
    return dispatch({
      type: t.INVALID_INPUT_VALUE,
      result,
    });
  }
  return dispatch({
    type: t.VALIDATE_FORM_SUBMISSION,
    result,
  });
};

export const handleCheckedSearchChange = result => (dispatch) => {
  const defaults = {
    arrivalCheckedSearch: {
      arrivalAirports: [],
      arrivalCountries: [],
      arrivalRegions: [],
      arrivalCheckedSearchInvalid: null,
    },
    departureCheckedSearch: {
      departureAirports: [],
      departureCountries: [],
      departureRegions: [],
      departureCheckedSearchInvalid: null,
    },
  };
  const newResult = {
    ...result,
    ...defaults[Object.keys(result)[0]],
  };
  return dispatch({
    type: t.UPDATE_CHECKED_SEARCH,
    result: newResult,
  });
};

export const getServerDate = () =>
  async (dispatch, getStore, client) => {
    dispatch(dispatchOnlyType(t.GET_SERVER_DATE));
    try {
      const result = await client.get('/utils/current-date');
      return dispatch({
        type: t.GET_SERVER_DATE_SUCCESS,
        result,
      });
    } catch (err) {
      return dispatch(dispatchOnlyType(t.GET_SERVER_DATE_FAIL));
    }
  };

export const handleFormInputValue = (input = {}) => {
  const {
    value, name, type, skipValidation,
  } = input;
  const validationCallback = validationCallbacks[type];
  const invalid = validationCallback(value);
  if (!invalid || skipValidation) {
    return {
      type: t.UPDATE_INPUT_VALUE,
      result: { [name]: value, [`${name}Invalid`]: null },
    };
  }
  return {
    type: t.INVALID_INPUT_VALUE,
    result: { [name]: value, [`${name}Invalid`]: invalid },
  };
};

export const updateAirlineMultiselect = result => dispatch => dispatch({
  type: t.UPDATE_AIRLINE_SELECT,
  result,
});

export const updateAirportMultiselect = result => (dispatch) => {
  const { identifier } = result;
  const errorType = identifier.replace(/Regions|Airports|Countries/, 'CheckedSearchInvalid');
  return dispatch({
    type: t.UPDATE_AIRPORT_SELECT,
    result: {
      ...result,
      clearError: errorType,
    },
  });
};

export const updateSyncMultiselect = result => (dispatch) => {
  const { identifier } = result;
  const errorType = identifier.replace(/Regions|Airports|Countries/, 'CheckedSearchInvalid');
  return dispatch({
    type: t.UPDATE_SYNC_MULTISELECT,
    result: {
      ...result,
      clearError: errorType,
    },
  });
};

export const handleDataEventRefresh = () =>
  async (dispatch, getStore, client) => {
    const url = '/data-exports';
    dispatch(dispatchOnlyType(t.LOAD_REFRESH_EXPORTS));
    try {
      const result = await client.get(url);
      const { data } = result;
      const store = getStore();
      const currentId = store && store[NAME] && store[NAME].currentlyGeneratingExportId;
      const filteredData = filterQuoteOnlyStatuses(data, currentId);
      return dispatch({
        type: t.LOAD_REFRESH_EXPORTS_SUCCESS,
        result: filteredData,
      });
    } catch (err) {
      return dispatch(dispatchOnlyType(t.LOAD_REFRESH_EXPORTS_FAIL));
    }
  };
// *** actions for /home (previous data exports page) ***

export const openOption = index => dispatch => Promise.resolve(dispatch({
  type: t.PREV_EXPORTS_OPEN_OPTION,
  index,
}));

export const createDataExportEvent = (type, id) =>
  async (dispatch, getStore, client) => {
    const data = {
      type,
      id,
    };
    const url = '/data-exports/events/create';
    dispatch(dispatchOnlyType(t.CREATE_DATA_EXPORT_EVENT));
    try {
      const result = client.post(url, { data });
      return dispatch({
        type: t.CREATE_DATA_EXPORT_EVENT_SUCCESS,
        result,
      });
    } catch (err) {
      return dispatch(dispatchOnlyType(t.CREATE_DATA_EXPORT_EVENT_FAIL));
    }
  };

export const dismissLimitedDataExportSearch = dataExport => (dispatch) => {
  const result = {
    ...dataExport,
    dismissed: true,
  };
  return dispatch({
    type: t.DISMISS_LIMITED_SEARCH,
    result,
  });
};

export const softDataExportDelete = id => async (dispatch, getStore, client) => {
  const url = `/data-exports/${id}`;
  dispatch(dispatchOnlyType(t.DELETE_DATA_EXPORT));
  try {
    const result = await client.del(url);
    return dispatch({
      type: t.DELETE_DATA_EXPORT_SUCCESS,
      result,
    });
  } catch (err) {
    return dispatch(dispatchOnlyType(t.DELETE_DATA_EXPORT_FAIL));
  }
};

export const removeDataExport = (prevExports, id) => (dispatch) => {
  const dataExportQuoteHistory = prevExports.filter(dataExport => dataExport.id !== id);
  return dispatch({
    type: t.REMOVE_DATA_EXPORT,
    dataExportQuoteHistory,
  });
};

export const openDeleteExportModal = id => dispatch => dispatch({
  type: t.OPEN_DELETE_EXPORT_MODAL,
  id,
});

export const closeDeleteExportModal = () => dispatch =>
  dispatch(dispatchOnlyType(t.CLOSE_DELETE_EXPORT_MODAL));

export const clearCurrentPollingExportId = () => dispatch =>
  dispatch(dispatchOnlyType(t.CLEAR_POLLING_EXPORT_ID));

// *** end actions for /home (previous data exports page) ***



// WEBPACK FOOTER //
// ./modules/DataExports/redux/actions.js